function addClasses(element, ...classes) {
    const classNames = classes.flatMap((className) => {
        if (!className) {
            return [];
        }
        return Array.isArray(className)
            ? className
            : className.split(' ');
    }).filter(Boolean);
    element.classList.add(...classNames);
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
export class Toaster {
    version = '2.0.3';
    options = {};
    container = null;
    listener;
    toastId = 0;
    previousToast = null;
    controllers = new Map();
    clear(toastElement, clearOptions = {}) {
        if (toastElement) {
            const controller = this.controllers.get(toastElement);
            if (controller) {
                controller.hide(clearOptions.force ?? false);
                return;
            }
        }
        for (const controller of Array.from(this.controllers.values())) {
            controller.hide(false);
        }
        this.removeContainerIfEmpty();
    }
    remove(toastElement) {
        if (toastElement) {
            if (this.containsFocusedElement(toastElement)) {
                return;
            }
            const controller = this.controllers.get(toastElement);
            controller?.remove();
            this.removeContainerIfEmpty();
            return;
        }
        for (const controller of Array.from(this.controllers.values())) {
            controller.remove();
        }
        this.removeContainerIfEmpty();
    }
    error(message, title, optionsOverride) {
        return this.notify({
            type: 'error',
            iconClass: this.getOptions(optionsOverride ?? undefined).iconClasses.error,
            message,
            optionsOverride: optionsOverride ?? undefined,
            title: title ?? undefined,
        });
    }
    getContainer(options, create = false) {
        const resolved = this.getOptions();
        const containerOptions = { ...resolved, ...options };
        const existing = document.querySelector(`.${containerOptions.containerClass}`);
        if (existing instanceof HTMLElement) {
            this.container = existing;
            this.container.className = '';
            addClasses(this.container, containerOptions.containerClass);
            addClasses(this.container, containerOptions.positionClass);
            return this.container;
        }
        if (!create) {
            return null;
        }
        const container = document.createElement('div');
        addClasses(container, containerOptions.containerClass);
        addClasses(container, containerOptions.positionClass);
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('role', 'alert');
        const target = this.resolveTarget(containerOptions.target);
        target.appendChild(container);
        this.container = container;
        return container;
    }
    info(message, title, optionsOverride) {
        return this.notify({
            type: 'info',
            iconClass: this.getOptions(optionsOverride ?? undefined).iconClasses.info,
            message,
            optionsOverride: optionsOverride ?? undefined,
            title: title ?? undefined,
        });
    }
    subscribe(callback) {
        this.listener = callback;
    }
    success(message, title, optionsOverride) {
        return this.notify({
            type: 'success',
            iconClass: this.getOptions(optionsOverride ?? undefined).iconClasses.success,
            message,
            optionsOverride: optionsOverride ?? undefined,
            title: title ?? undefined,
        });
    }
    warning(message, title, optionsOverride) {
        return this.notify({
            type: 'warning',
            iconClass: this.getOptions(optionsOverride ?? undefined).iconClasses.warning,
            message,
            optionsOverride: optionsOverride ?? undefined,
            title: title ?? undefined,
        });
    }
    dispose() {
        this.clear();
        this.listener = undefined;
        this.controllers.clear();
        if (this.container?.parentElement) {
            this.container.parentElement.removeChild(this.container);
        }
        this.container = null;
    }
    add(type, message, title, optionsOverride) {
        return this.notify({
            type,
            iconClass: this.getIconClass(type, this.getOptions(optionsOverride ?? undefined)),
            message,
            optionsOverride: optionsOverride ?? undefined,
            title: title ?? undefined,
        });
    }
    notify(map) {
        const options = this.getOptions(map.optionsOverride);
        const iconClass = map.iconClass || options.iconClass;
        const messageKey = map.message ?? '';
        if (options.preventDuplicates && messageKey === this.previousToast) {
            return null;
        }
        this.previousToast = messageKey;
        this.toastId += 1;
        const container = this.getContainer(options, true);
        if (!container) {
            return null;
        }
        const toastElement = document.createElement('div');
        const titleElement = document.createElement('div');
        const messageElement = document.createElement('div');
        const closeElement = this.createCloseElement(options.closeHtml);
        const progressWrapperElement = document.createElement('div');
        const progressBarElement = document.createElement('div');
        const response = {
            toastId: this.toastId,
            state: 'visible',
            startTime: new Date(),
            options,
            map,
        };
        let timeoutHandle;
        let hideTimerHandle;
        let progressIntervalHandle;
        let hideEta = 0;
        let maxHideTime = 0;
        toastElement.style.display = 'block';
        toastElement.style.opacity = '0';
        toastElement.style.transition = `opacity ${options.showDuration}ms ease`;
        toastElement.setAttribute('data-toast-id', String(response.toastId));
        addClasses(toastElement, 'alert', map.type === 'error' ? 'alert-danger' : `alert-${map.type}`);
        addClasses(toastElement, options.toastClass, iconClass);
        if (options.rtl) {
            addClasses(toastElement, 'rtl');
        }
        if (map.title) {
            addClasses(titleElement, options.titleClass);
            titleElement.innerHTML = options.escapeHtml ? escapeHtml(map.title) : map.title;
            toastElement.appendChild(titleElement);
        }
        if (map.message) {
            addClasses(messageElement, options.messageClass);
            messageElement.innerHTML = options.escapeHtml ? escapeHtml(map.message) : map.message;
            toastElement.appendChild(messageElement);
        }
        const stopProgress = () => {
            if (progressIntervalHandle !== undefined) {
                window.clearInterval(progressIntervalHandle);
                progressIntervalHandle = undefined;
            }
            if (options.progressBar) {
                progressBarElement.style.width = '0%';
                progressWrapperElement.setAttribute('aria-valuenow', '0');
            }
        };
        const updateProgress = () => {
            if (!options.progressBar || maxHideTime <= 0) {
                return;
            }
            const percentage = ((hideEta - Date.now()) / maxHideTime) * 100;
            const clamped = Math.max(0, Math.min(100, percentage));
            progressBarElement.style.width = `${clamped}%`;
            progressWrapperElement.setAttribute('aria-valuenow', String(Math.round(clamped)));
        };
        const startProgress = (duration) => {
            if (!options.progressBar || duration <= 0) {
                stopProgress();
                return;
            }
            maxHideTime = duration;
            hideEta = Date.now() + duration;
            progressBarElement.style.width = '100%';
            updateProgress();
            if (progressIntervalHandle !== undefined) {
                window.clearInterval(progressIntervalHandle);
            }
            progressIntervalHandle = window.setInterval(() => {
                updateProgress();
            }, 10);
        };
        const showToast = () => {
            requestAnimationFrame(() => {
                toastElement.style.opacity = '0.8';
            });
        };
        const removeToast = () => {
            this.controllers.delete(toastElement);
            stopProgress();
            toastElement.remove();
            if (this.container?.children.length === 0) {
                this.container.remove();
                this.container = null;
                this.previousToast = null;
            }
        };
        const hideToast = (override = false) => {
            if (this.containsFocusedElement(toastElement) && !override) {
                return;
            }
            window.clearTimeout(timeoutHandle);
            window.clearTimeout(hideTimerHandle);
            stopProgress();
            toastElement.style.transition = `opacity ${options.hideDuration}ms ease`;
            toastElement.style.opacity = '0';
            hideTimerHandle = window.setTimeout(() => {
                if (response.state !== 'hidden') {
                    options.onHidden?.();
                }
                response.state = 'hidden';
                response.endTime = new Date();
                this.publish(response);
                removeToast();
            }, options.hideDuration);
        };
        const scheduleHide = (delay) => {
            window.clearTimeout(timeoutHandle);
            if (delay > 0) {
                timeoutHandle = window.setTimeout(hideToast, delay);
            }
        };
        const delayedHideToast = () => {
            if (options.extendedTimeOut > 0) {
                scheduleHide(options.extendedTimeOut);
                startProgress(options.extendedTimeOut);
            }
        };
        const stickAround = () => {
            window.clearTimeout(timeoutHandle);
            window.clearTimeout(hideTimerHandle);
            stopProgress();
            toastElement.style.transition = `opacity ${options.showDuration}ms ease`;
            toastElement.style.opacity = '0.8';
        };
        if (options.closeButton) {
            addClasses(closeElement, 'btn-close', options.closeClass);
            closeElement.setAttribute('role', 'button');
            closeElement.addEventListener('click', (event) => {
                event.stopPropagation();
                options.onCloseClick?.(event);
                hideToast(true);
            });
            toastElement.prepend(closeElement);
        }
        if (options.progressBar && options.timeOut > 0) {
            addClasses(progressWrapperElement, 'progress', 'toast-progress');
            progressWrapperElement.setAttribute('role', 'progressbar');
            progressWrapperElement.setAttribute('aria-label', 'Toast timeout');
            progressWrapperElement.setAttribute('aria-valuemin', '0');
            progressWrapperElement.setAttribute('aria-valuemax', '100');
            progressWrapperElement.setAttribute('aria-valuenow', '100');
            addClasses(progressBarElement, 'progress-bar');
            progressBarElement.style.width = '100%';
            progressWrapperElement.appendChild(progressBarElement);
            toastElement.appendChild(progressWrapperElement);
        }
        this.controllers.set(toastElement, {
            element: toastElement,
            response,
            hide: hideToast,
            remove: removeToast,
        });
        if (options.newestOnTop) {
            container.prepend(toastElement);
        }
        else {
            container.appendChild(toastElement);
        }
        if (options.closeOnHover) {
            toastElement.addEventListener('mouseenter', stickAround);
            toastElement.addEventListener('mouseleave', delayedHideToast);
        }
        if (!options.onclick && options.tapToDismiss) {
            toastElement.addEventListener('click', () => hideToast());
        }
        if (options.onclick) {
            toastElement.addEventListener('click', () => {
                options.onclick?.();
                hideToast();
            });
        }
        showToast();
        options.onShown?.();
        scheduleHide(options.timeOut);
        startProgress(options.timeOut);
        this.publish(response);
        if (options.debug && typeof console !== 'undefined') {
            console.warn(response);
        }
        return toastElement;
    }
    publish(response) {
        this.listener?.(response);
    }
    getIconClass(type, options) {
        switch (type) {
            case 'error':
                return options.iconClasses.error;
            case 'info':
                return options.iconClasses.info;
            case 'success':
                return options.iconClasses.success;
            case 'warning':
                return options.iconClasses.warning;
            default:
                return options.iconClasses.info;
        }
    }
    getOptions(optionsOverride) {
        const defaults = this.getDefaults();
        const options = {
            ...defaults,
            ...this.options,
            iconClasses: {
                ...defaults.iconClasses,
                ...this.options.iconClasses,
            },
        };
        if (optionsOverride) {
            const { iconClasses, ...rest } = optionsOverride;
            Object.assign(options, rest);
            if (iconClasses) {
                options.iconClasses = {
                    ...options.iconClasses,
                    ...iconClasses,
                };
            }
        }
        return options;
    }
    getDefaults() {
        return {
            tapToDismiss: true,
            toastClass: 'toast',
            containerClass: 'toast-container',
            debug: false,
            showMethod: 'fadeIn',
            showDuration: 300,
            showEasing: 'swing',
            onShown: undefined,
            hideMethod: 'fadeOut',
            hideDuration: 1000,
            hideEasing: 'swing',
            onHidden: undefined,
            extendedTimeOut: 1000,
            iconClasses: {
                error: 'toast-error',
                info: 'toast-info',
                success: 'toast-success',
                warning: 'toast-warning',
            },
            iconClass: 'toast-info',
            positionClass: 'toast-top-right',
            timeOut: 5000,
            titleClass: 'toast-title',
            messageClass: 'toast-message',
            target: 'body',
            closeHtml: '<button type="button" aria-label="Close"></button>',
            newestOnTop: true,
            closeButton: false,
            closeClass: 'btn-close-toast',
            closeOnHover: true,
            progressBar: false,
            escapeHtml: false,
            preventDuplicates: false,
            rtl: false,
        };
    }
    createCloseElement(closeHtml) {
        const template = document.createElement('template');
        template.innerHTML = closeHtml.trim();
        const element = template.content.firstElementChild;
        if (element instanceof HTMLElement) {
            element.setAttribute('aria-label', element.getAttribute('aria-label') || 'Close');
            return element;
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', 'Close');
        return button;
    }
    resolveTarget(target) {
        const node = document.querySelector(target);
        if (node) {
            return node;
        }
        return document.body;
    }
    removeContainerIfEmpty() {
        if (this.container && this.container.children.length === 0) {
            this.container.remove();
            this.container = null;
            this.previousToast = null;
        }
    }
    containsFocusedElement(element) {
        return element.contains(document.activeElement);
    }
}
